import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getFuelStatus } from '../services/api';

const FuelBadge = ({ fuelStatus, thresholdLiters }) => {
  if (!fuelStatus?.configured) {
    return <span className="badge">No fill data</span>;
  }
  if (fuelStatus.empty) {
    return <span className="badge danger">Empty / overdue</span>;
  }
  if (fuelStatus.lowFuel) {
    return <span className="badge warn">Low fuel (≤{thresholdLiters} L)</span>;
  }
  return <span className="badge ok">OK</span>;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd MMM yyyy');
  } catch (_err) {
    return '—';
  }
};

export default function FuelStatus() {
  const [data, setData] = useState({ thresholdLiters: 10, vehicles: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFuelStatus();
      setData({
        thresholdLiters: res?.thresholdLiters ?? res?.thresholdKm ?? 10,
        vehicles: Array.isArray(res?.vehicles) ? res.vehicles : [],
      });
    } catch (err) {
      setError(err?.message || 'Failed to load fuel status');
      setData({ thresholdLiters: 10, vehicles: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const lowCount = data.vehicles.filter((v) => v.fuelStatus?.lowFuel).length;

  return (
    <>
      <div className="page-header">
        <h2>Fuel Status</h2>
        <p>
          Estimated fuel left based on last fill, vehicle km/L, and km driven since fill.
          Cars/vans alert at about <strong>{data.thresholdLiters} L</strong>; bikes use <strong>2 L</strong>.
          {' '}
          <Link to="/fuel-fill">Log a fill →</Link>
        </p>
      </div>

      <div className="card">
        {loading ? (
          <p className="text-muted">Loading fuel status…</p>
        ) : error ? (
          <p className="text-muted">{error}</p>
        ) : (
          <>
            {lowCount > 0 && (
              <p className="text-sm" style={{ marginBottom: 12, color: 'var(--warn, #f59e0b)' }}>
                <strong>{lowCount}</strong> vehicle{lowCount === 1 ? '' : 's'} need fill-up soon.
              </p>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Plate</th>
                    <th>Category</th>
                    <th>Fuel avg</th>
                    <th>Last fill</th>
                    <th>Liters left</th>
                    <th>Km left</th>
                    <th>Threshold</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vehicles.map((v) => {
                    const low = v.fuelStatus?.lowFuel;
                    const empty = v.fuelStatus?.empty;
                    const threshold = v.thresholdLiters ?? data.thresholdLiters;
                    return (
                      <tr
                        key={v.vehicleId}
                        className={empty ? 'wash-row-overdue' : (low ? 'wash-row-due' : undefined)}
                      >
                        <td>{v.name}</td>
                        <td className="mono text-muted">{v.plate}</td>
                        <td className="text-muted">{v.vehicleCategory || '—'}</td>
                        <td>{v.fuelAverage ? `${v.fuelAverage} km/L` : '—'}</td>
                        <td>{formatDate(v.lastFill?.fillDate)}</td>
                        <td>
                          {v.fuelStatus?.configured
                            ? (v.fuelStatus.litersRemaining ?? '—')
                            : '—'}
                        </td>
                        <td>
                          {v.fuelStatus?.configured
                            ? (v.fuelStatus.kmRemaining ?? '—')
                            : '—'}
                        </td>
                        <td>{threshold != null ? `${threshold} L` : '—'}</td>
                        <td>
                          <FuelBadge
                            fuelStatus={v.fuelStatus}
                            thresholdLiters={threshold}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
